import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import dataService from "../services/data.service";
import { UPDATE_STATE } from "../actions/types";
import { APP_CACHE } from "../app/config-v4-api";
import { deepCopy } from "../app/util";

//This hook is a helper hook to check online status of the user
function useOnlineStatus(params) {
  const [isOnline, setIsOnline] = useState(window.navigator.onLine);
  // debugger;
  useEffect(() => {
    function handleOnline() {
      setIsOnline(true);
    }
    function handleOffline() {
      setIsOnline(false);
    }
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}


const useVisbilityState = (val) => {
  const [visibilityState, setVisibilityState] = useState(val || 'visible');

  const handleVisbilityChange = useCallback(() => {
    setVisibilityState(document.visibilityState);
  }, [setVisibilityState]);

  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisbilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisbilityChange);
  }, [handleVisbilityChange]);

  return visibilityState;
};

const useRecalibrateStatus = (props) => {
  const { app, auth } = useSelector((state) => state);
  const { app_state, ui_state } = app.current_state;

  const { pricingSeasonsUI } = ui_state;
  const { interval = 10, property_id, isFirstTime } = props;
  const [isRecalibrating, setIsRecalibrating] = useState(false);
  const [result, setResult] = useState(null);
  const compRef = useRef({ property_id: app_state.property_id, calibrateStatus: null, apiCallCount: 0, isFirstTime: isFirstTime });
  let isPropertyChanged = compRef.current.property_id !== app_state.property_id;
  const dispatch = useDispatch();
  let apc = APP_CACHE;
  const { property_details } = auth.profile;
  let prop_details_index = 0;
  const prop_details = property_details.find((item, index) => {
    if (item.id === app_state.property_id) {
      prop_details_index = index;
      return item.id === app_state.property_id;
    }
  });

  const { permissions } = prop_details;
  APP_CACHE.pricingSeasonsUI = deepCopy(pricingSeasonsUI);

  const updatePricingSeasonsUI = (delta) => {
    let apc = APP_CACHE;
    dispatch({
      type: UPDATE_STATE,
      payload: {
        ui_state: {
          pricingSeasonsUI: {
            ...ui_state.pricingSeasonsUI,
            ...apc.pricingSeasonsUI,
            ...delta,
          },
        }
      }
    });
    dispatch({
      type: UPDATE_STATE,
      payload: {
        ui_state: {
          pricingSeasonsUI: {
            ...ui_state.pricingSeasonsUI,
            ...apc.pricingSeasonsUI,
            ...delta,
          },
        }
      }
    });
  }
  //Clear all intervals
  const clearAllIntervals = (id) => {
    // Clear any timeout/interval up to that id
    if (compRef.current.calibrateStatus) {
      for (let i = 1; i < id; i++) {
        clearInterval(i);
        clearTimeout(i);
      }
      compRef.current.calibrateStatus = null;
    }
  };

  const getRecalibrationStatus = (property_id) => {
    let requestAPI = "rms/properties/recalibrate/pricing-seasons/" + (property_id) + "/";
    compRef.current.apiCallCount += 1;
    return dataService.fetchDataSimple({ api: requestAPI, request: { method: 'GET' } })
  }
  let avoidToast = false;

  const handleSuccess = (rs) => {
    compRef.current.apiCallInProgress = false;
    if (rs && rs.status === 'error') {
      handleError(rs.message);
    } else
      if (rs && rs.status === 'completed') {
        apc.pricingSeasonsUI.isRecalibrating = false;
        apc.pricingSeasonsUI.recalibrationStatus = 'done';
        updatePricingSeasonsUI({ isRecalibrating: false, recalibrationStatus: 'done' });
        clearInterval(compRef.current.calibrateStatus);

        if (compRef.current.apiCallCount > 1) {
          if (!avoidToast) {
            toast.success(<span><strong>
              Sell Rate Recalibration Completed for {app_state.property_name}.</strong> <br />
              Recommendations Are Now up to Date</span>, {
              position: "bottom-right",
              autoClose: 5000,
            });
          }
        }
        return;
      } else
        if (rs && rs.status === 'in-progress') {
          apc.pricingSeasonsUI.isRecalibrating = true;
          apc.pricingSeasonsUI.recalibrationStatus = 'in-progress';
          updatePricingSeasonsUI({ isRecalibrating: true, recalibrationStatus: 'in-progress' });
          clearAllIntervals(compRef.current.calibrateStatus);
          if (isRecalibrating) setIsRecalibrating(isRecalibrating + 1);
          if (!isRecalibrating) setIsRecalibrating(1);

        } else {
          apc.pricingSeasonsUI.isRecalibrating = false;
          apc.pricingSeasonsUI.recalibrationStatus = rs.status;
        }
  }

  const handleError = (err) => {
    pricingSeasonsUI.isRecalibrating = false;
    updatePricingSeasonsUI({ isRecalibrating: false });
    toast.error(<span><strong>{err.toString()}</strong></span>, {
      position: "bottom-right",
      autoClose: 3000,
    });
  }

  useEffect(() => {
    const { pricing_model } = permissions;
    const { date_range_type } = app_state;
    const { apiCallInProgress, isFirstTime } = compRef.current;
    isPropertyChanged = compRef.current.property_id !== app_state.property_id;
    // Clear intervals if property is swiching


    if (pricing_model) {
      if ((isFirstTime || isPropertyChanged) && !apiCallInProgress) {
        clearAllIntervals(compRef.current.calibrateStatus);
        compRef.current.isFirstTime = false;
        isPropertyChanged = false;
        compRef.current.apiCallCount = 0;

        compRef.current.property_id = app_state.property_id;
        // if (apc.pricingSeasonsUI.recalibrationStatus !== 'done') {
        compRef.current.calibrateStatus = setTimeout(() => {
          compRef.current.apiCallInProgress = true;
          getRecalibrationStatus(compRef.current.property_id)
            .then(
              (rs) => {
                compRef.current.apiCallInProgress = false;
                handleSuccess(rs);
              },
              (err) => {
                compRef.current.apiCallInProgress = false;
                handleError(err);
              })
        }, 400);
        // }
      } else { //
        clearAllIntervals(compRef.current.calibrateStatus);
        compRef.current.calibrateStatus = setTimeout(() => {
          if (['completed', 'not found', 'done'].includes(apc.pricingSeasonsUI.recalibrationStatus)) {
            clearAllIntervals(compRef.current.calibrateStatus);
          }
          if (!['completed', 'not found', 'done'].includes(apc.pricingSeasonsUI.recalibrationStatus) && !compRef.current.apiCallInProgress) {
            compRef.current.apiCallInProgress = true;
            getRecalibrationStatus(compRef.current.property_id)
              .then(
                (rs) => {
                  compRef.current.apiCallInProgress = false;
                  handleSuccess(rs);
                },
                (err) => {
                  compRef.current.apiCallInProgress = false;
                  handleError(err);
                })
          }
        }, interval * 1000);
      }
    }
    else if (!pricing_model) {
      clearAllIntervals(compRef.current.calibrateStatus);
    }

    return () => {
      // clearInterval(compRef.current.calibrateStatus);
      // clearAllIntervals(compRef.current.calibrateStatus);
      // apc.pricingSeasonsUI.recalibrationStatus = 'initial';
      // updatePricingSeasonsUI({recalibrationStatus: 'initial'});
    }

  }, [permissions, isRecalibrating, pricingSeasonsUI.isRecalibrating]);


  useEffect(() => {
    let apc = APP_CACHE;
    if (permissions.pricing_model && !app_state?.date_range_type?.includes("switchingProperty")) {
      compRef.current.isFirstTime = true;
      clearAllIntervals(compRef.current.calibrateStatus);
    }
  }, [permissions]);

  // Memoize returned object
  const value = useMemo(() => ({
    isRecalibrating: pricingSeasonsUI.isRecalibrating,
    data: result
  }), [pricingSeasonsUI.isRecalibrating]);

  return value;
};

export {
  useOnlineStatus,
  useVisbilityState,
  useRecalibrateStatus
}
